import type { Editor } from "@tiptap/core"
import type { EditorOpenMenuEvent } from "./open-menu-event"
import type { EditorOperationEvent } from "./operation-event"
import type { HostId } from "./types"

/**
 * The best-effort sink for editor analytics events: synchronous, post-mutation, return value
 * ignored, and called only from behind the failure boundary in instrument.ts.
 */
export interface EditorAnalyticsPort {
  recordOperation(event: EditorOperationEvent): void
  recordOpenMenu(event: EditorOpenMenuEvent): void
}

/**
 * Per-editor analytics runtime: the required port, the host identity every event carries, and an
 * error sink for the failure boundary. Editable lesson editors construct one (see UnifiedCardStackEditor);
 * other editors have none, and {@link guardAnalytics} skips every emit path for them.
 */
export interface AnalyticsRuntime {
  readonly hostId: HostId
  readonly port: EditorAnalyticsPort
  /** Where the failure boundary reports swallowed throws (Sentry in prod). */
  readonly onError: (error: unknown) => void
}

// The runtime is attached to the Editor instance out-of-band (not through a Tiptap extension
// storage slot) so it can be set once at construction and read from any analytics path without
// widening the extension option surface. A WeakMap keeps it from pinning destroyed editors.
const runtimes = new WeakMap<Editor, AnalyticsRuntime>()

/** Attach an editor's analytics runtime. Called once, at construction, for editable editors. */
export function setAnalyticsRuntime(editor: Editor, runtime: AnalyticsRuntime): void {
  runtimes.set(editor, runtime)
}

/**
 * The analytics failure boundary.
 *
 * Errors are reported to the editor's runtime's onError.
 * Skips if the editor does not have a runtime (i.e., it is read-only).
 *
 * @return The output of `work(runtime)`, or undefined if it was skipped or errored.
 */
export function guardAnalytics<T>(
  editor: Editor,
  work: (runtime: AnalyticsRuntime) => T,
): T | undefined {
  const runtime = runtimes.get(editor)
  if (!runtime) return undefined
  try {
    return work(runtime)
  } catch (error) {
    try {
      runtime.onError(error)
    } catch {
      // Ignore nested errors.
    }
    return undefined
  }
}
